import { useQuery, useMutation } from "@apollo/client/react";
import {
  GetContractsDocument,
  GetContractDocument,
  UpdateContractsDocument,
  DeleteContractsDocument,
  type GetContractsQuery,
  type GetContractQuery,
  type UpdateContractsMutation,
  type DeleteContractsMutation,
  type Contracts,
  type ContractsUpdateInput,
} from "@/src/gql/graphql";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";
import { serializeGraphqlInput } from "@/lib/graphql/serialize-input";
// Writes go through pg_graphql, not an API route, so the server-side
// mapToUserFacingError never sees them. See mutation-error.ts.
import { crmMutationError } from "@/lib/utils/crm/mutation-error";

/**
 * Contracts are READ-mostly: a contract is a real e-signature document created
 * by the dedicated contract send flow, never by the generic CRM create form.
 * So `useCreate` throws; `useUpdate` (manual edits — link to a deal, status
 * correction) and `useDelete` (admin) are supported.
 *
 * Apollo + usePaginatedCollection (cache-and-network) + serializeGraphqlInput,
 * mirroring `contacts-graphql.ts`. The ContractFields fragment selects every
 * displayed column, so the codegen result node carries fragment refs — cast the
 * resolved node to `Contracts` at the read boundary.
 */

/** Drop Apollo cache metadata (`__typename`, fragment-ref keys) before a mutation. */
function cleanContractUpdateInput(input: Partial<Contracts>): ContractsUpdateInput {
  const entries = Object.entries(input).filter(([key]) => !key.startsWith("__") && key !== "id");
  return Object.fromEntries(entries);
}

export const useContractHooks = (): EntityHooks<Contracts> => ({
  queryDocument: GetContractsDocument,

  usePaginated: (params?: Record<string, unknown>) =>
    // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
    usePaginatedCollection(
      GetContractsDocument,
      params,
      (data: GetContractsQuery) => data.contractsCollection,
      (node) => node as unknown as Contracts
    ),

  useInfinite: (_params?: Record<string, unknown>) => ({
    data: undefined,
    isLoading: false,
    error: null,
    refetch: () => {},
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: () => {},
  }),

  useSingle: (id: string, options?: { enabled?: boolean }) => {
    const result = useQuery<GetContractQuery>(GetContractDocument, {
      variables: { id },
      errorPolicy: "all",
      skip: !id || (options && options.enabled === false),
    });
    const node = result.data?.contractsCollection?.edges?.[0]?.node;
    return {
      data: node ? (node as unknown as Contracts) : undefined,
      isLoading: result.loading,
      error: result.error || null,
      refetch: result.refetch,
    };
  },

  useUpdate: () => {
    const [mutate, result] = useMutation<UpdateContractsMutation>(UpdateContractsDocument);

    return {
      mutateAsync: async ({ id, ...input }: { id: string } & Partial<Contracts>) => {
        const cleanInput = serializeGraphqlInput(
          "contracts",
          cleanContractUpdateInput(input)
        );
        const { data, error } = await mutate({
          variables: { id, input: cleanInput },
          errorPolicy: "all",
        });

        if (error) throw crmMutationError(error, "Couldn't update the contract.");

        const updated = data?.updatecontractsCollection?.records?.[0];
        if (!updated) throw new Error("Couldn't update the contract.");
        return updated as unknown as Contracts;
      },
      isPending: result.loading,
    };
  },

  useDelete: () => {
    const [mutate, result] = useMutation<DeleteContractsMutation>(DeleteContractsDocument);
    return {
      mutateAsync: async (id: string) => {
        const { data, error } = await mutate({
          variables: { id },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't delete the contract.");
        const deletedId = data?.deleteFromcontractsCollection?.records?.[0]?.id;
        if (!deletedId) throw new Error("Couldn't delete the contract.");
        return deletedId;
      },
      isPending: result.loading,
    };
  },

  useCreate: () => ({
    mutateAsync: async () => {
      throw new Error(
        "Contracts are created via the contract send flow, not the generic form"
      );
    },
    isPending: false,
  }),
});
